import { PowerSyncDatabase } from "@powersync/capacitor";
import { Schema, Table, WASQLiteVFS, column, type PowerSyncBackendConnector } from "@powersync/web";
import { SAVE_REFUSED_EVENT, getAuthToken, reconnectWhenModulesChange, syncConnector } from "@cliqsuite/db/powersync";

// Only the columns the kitchen reads. PowerSync keeps the rest of each row but doesn't expose it.
// The board re-runs on every tap and every synced row, so without these indexes each tap scans the store's whole order history.
const schema = new Schema({
  kitchen_tickets: new Table({ store_id: column.text, order_id: column.text, status: column.text, done_at: column.text, nudged_at: column.text, created_at: column.text, updated_at: column.text }, { indexes: { board: ["store_id", "status"] } }),
  orders: new Table({ order_number: column.text, order_type: column.text, source: column.text, table_id: column.text, customer_name: column.text, staff_id: column.text, notes: column.text }),
  order_items: new Table({ kitchen_ticket_id: column.text, product_name: column.text, variant_label: column.text, quantity: column.integer, notes: column.text, option_labels: column.text, is_voided: column.integer, ready_at: column.text, served_at: column.text, created_at: column.text, updated_at: column.text }, { indexes: { ticket: ["kitchen_ticket_id"] } }),
  tables: new Table({ table_number: column.integer }),
  staff: new Table({ name: column.text }),
  devices: new Table({ is_paired: column.integer, device_name: column.text }),
  businesses: new Table({ installed_modules: column.text, subscription_status: column.text, subscription_period_end: column.text, grace_period_ends_at: column.text }),
});

// A kitchen screen is one tab on a kiosk. Multi-tab mode syncs from a SharedWorker, which some
// kiosk browsers (Android WebView, embedded panes) lack or block, so sync runs in this tab instead.
// Safari overflows the stack on the default IndexedDB file system, so it gets PowerSync's recommended OPFS one.
// Inside the installed app (Capacitor on Android and iOS) the same class uses native SQLite and ignores the vfs.
const safari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
export const db = new PowerSyncDatabase({ schema, database: { dbFilename: "kds.db", enableMultiTabs: false, vfs: safari ? WASQLiteVFS.OPFSCoopSyncVFS : WASQLiteVFS.IDBBatchAtomicVFS } });

// PowerSync applies nothing new while a write waits to upload, so one stuck tap would freeze this
// screen. The board matters more than the tap: after 15 seconds it's dropped, with the failed-save
// notice, and the cook taps again.
const GIVE_UP_MS = 15_000;
export function kdsConnector(): PowerSyncBackendConnector {
  const base = syncConnector();
  let failingSince = 0;
  return {
    ...base,
    async uploadData(database) {
      try {
        await base.uploadData(database);
        failingSince = 0;
      } catch (err) {
        failingSince ||= Date.now();
        if (Date.now() - failingSince < GIVE_UP_MS) throw err;
        await (await database.getNextCrudTransaction())?.complete();
        failingSince = 0;
        window.dispatchEvent(new Event(SAVE_REFUSED_EVENT));
      }
    },
  };
}

/**
 * Connect once a device-scope token exists (after pairing, or on boot if already paired). The kitchen
 * has no offline mode: every boot drops the local copy and its unsent taps and syncs fresh, so tickets
 * from before the screen slept can't flash up or be pushed back over what the till did since.
 */
export async function connectKds(): Promise<void> {
  if (!getAuthToken()) return;
  // The local copy is kept (unpairing clears it), so the board opens at once and shows offline, then
  // catches up in a moment. Taps left from last time are dropped, so they can't undo what the till did since.
  for (let tx = await db.getNextCrudTransaction(); tx; tx = await db.getNextCrudTransaction()) await tx.complete();
  void db.connect(kdsConnector());
  if (!watchingModules) reconnectWhenModulesChange(db, kdsConnector);
  watchingModules = true;
}
let watchingModules = false;

// The PGlite database the Electric version kept. Its only unsent data is kitchen status taps,
// which a cook simply repeats, so it's dropped rather than migrated.
try {
  indexedDB.deleteDatabase("/pglite/cliqsuite-kds");
} catch {
  /* private mode / no IndexedDB */
}
